import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { RotationImageComponent } from './rotation-image/rotation-image.component';
import { Image } from '../../../../models/image';
import { Subscription } from 'rxjs';
import { GalleryService } from '../../../../services/gallery.service';
import { SettingsService } from '../../../../services/settings.service';
import { SynchronizationService } from '../../../../services/synchronization.service';

@Component({
  selector: 'app-gallery-small-view',
  templateUrl: './gallery-view-small.component.html',
  styleUrls: ['./gallery-view-small.component.scss']
  })
export class GalleryViewSmallComponent implements OnInit, AfterViewInit, OnDestroy {
  private event: Subscription[] = [];
  selectedProject: String;

  public hasImages = true;
  @ViewChild('rotationImage1', { static: true }) private rotImg1: RotationImageComponent;
  @ViewChild('rotationImage2', { static: true }) private rotImg2: RotationImageComponent;
  @ViewChild('rotationImage3', { static: true }) private rotImg3: RotationImageComponent;
  @ViewChild('rotationImage4', { static: true }) private rotImg4: RotationImageComponent;
  @ViewChild('rotationImage5', { static: true }) private rotImg5: RotationImageComponent;
  private availableImages: Image[] = [];
  private rotationImageComponents: RotationImageComponent[];
  private rotationEvent: Subscription;
  private intervalDuration = 5000; // in ms
  private rotationModeOn = true;

  constructor(
    private galleryService: GalleryService,
    private settingsService: SettingsService,
    private synchronizationService: SynchronizationService,
  ) { }

  ngOnInit() {
    this.initCurrentProjectAndSprint();
    this.setupPersistenceEvents();
  }

  initCurrentProjectAndSprint(): void {
    this.selectedProject = this.settingsService.getProjectName();
  }

  ngAfterViewInit() {
    this.loadImages();
  }

  ngOnDestroy(): void {
    for (const ev of this.event) {
      ev.unsubscribe();
    }
  }

  /**
   * Load all images into the view and start the rotation if there are enough images.
   */
  private loadImages(): void {
    this.galleryService.getImages(this.selectedProject).subscribe(images => {
      const len = images.length;
      if (len === 0) {
        this.hasImages = false;
      } else {
        this.hasImages = true;
        this.availableImages = images;
      }

      this.stopImageRotation();
      this.initImageRotation();
      if (len > 3 && this.rotationModeOn) {
        this.startImageRotation();
      }
    });
  }

  /**
   * Initializes the position of random chosen images before starting the rotation.
   */
  private initImageRotation() {
    this.rotationImageComponents = [];

    this.rotImg5.initState('initialization');
    this.rotImg5.initImage(undefined);
    this.rotationImageComponents.push(this.rotImg5);

    this.rotImg1.initState('initialization');
    this.rotImg1.initState('hidden');
    this.rotImg1.initImage(undefined);
    this.rotationImageComponents.push(this.rotImg1);

    this.rotImg3.initState('initialization');
    this.rotImg3.initState('center');
    this.rotImg3.initImage(this.getRandomAvailableImage());
    this.rotationImageComponents.push(this.rotImg3);

    this.rotImg2.initState('initialization');
    this.rotImg2.initState('left');
    this.rotImg2.initImage(this.getRandomAvailableImage());
    this.rotationImageComponents.push(this.rotImg2);

    this.rotImg4.initState('initialization');
    this.rotImg4.initState('right');
    this.rotImg4.initImage(this.getRandomAvailableImage());
    this.rotationImageComponents.push(this.rotImg4);
  }

  /**
   * Set up the event listeners for persistence synchronization.
   */
  private setupPersistenceEvents() {
    this.event.push(this.synchronizationService.subscribeEvent<Image>('uploadImage').subscribe(() => {
      this.loadImages();
    }));
    this.event.push(this.synchronizationService.subscribeEvent<number>('deleteImage').subscribe(() => {
      this.loadImages();
    }));
  }

  /**
   * Starts the rotation.
   */
  private startImageRotation() {
    // TODO implement with rxjs-compat.TimerObservable!
    /*
    this.rotationEvent = TimerObservable.create(2000, this.intervalDuration).subscribe(() => {
      this.rotateImages();
    })
    */;
  }

  /**
   * Stops the rotation.
   */
  private stopImageRotation() {
    if (this.rotationEvent) {
      this.rotationEvent.unsubscribe();
    }
  }

  /**
   * Rotate all images by setting the for each image its next state.
   */
  private rotateImages() {
    const nextImage = this.getRandomAvailableImage();
    this.rotationImageComponents.forEach(image => image.rotate(nextImage, img => this.onRotationEndHandler(img)));
  }

  /**
   * Callback to set an image available again after it is no longer needed to be displayed in the rotation.
   * @param image The image which should be set available.
   */
  onRotationEndHandler(image: Image) {
    this.availableImages.push(image);
  }

  /**
   * Chooses a random image from the availableImages list.
   * @returns a random available image or undefined if there is no image available.
   */
  private getRandomAvailableImage() {
    const index = Math.floor(Math.random() * (this.availableImages.length));
    const img = this.availableImages[index];
    this.availableImages.splice(index, 1);
    return img;
  }
}
